import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useData } from '../context/DataContext';
import { X, ArrowRightLeft, Search, TrendingUp, PiggyBank, HelpingHand, Wallet } from 'lucide-react';
import { ModalAgregarCuenta } from './ModalAgregarCuenta';
import { Transaccion } from '../types';
import { calcularComisionAportacion, TIPO_INVERSION_ICON, TIPO_INVERSION_LABEL } from '../utils/inversionUtils';
import { limiteEnPeriodo, periodoDeFecha, presupuestoActivoEnPeriodo } from '../utils/presupuestoUtils';
import { CATEGORIA_ADELANTO, personasDeAdelantos } from '../utils/transaccionUtils';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE_POSITIVO } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';
import { vibrar } from '../utils/hapticos';
import { cambiosTarjeta, cuentaDeTarjeta, cuentaParecida, DatosApuntar, sugerenciaPorComercio } from '../utils/apuntarRapido';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';

type TipoForm = 'ingreso' | 'gasto' | 'traspaso';

interface Props {
  onClose: () => void;
  transaccionEditar?: Transaccion;
  /** Lo que llega de un Atajo o de Wallet (ver utils/apuntarRapido). */
  inicial?: DatosApuntar;
}

export const ModalAgregarTransaccion: React.FC<Props> = ({ onClose, transaccionEditar, inicial }) => {
  useCerrarConEscape(onClose);
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const { actualizarCuenta, crearTransaccion, actualizarTransaccion, crearTraspaso, crearTraspasoAInversion, cuentas, cuentasCargadas, categoriasCargadas, transaccionesCargadas, categorias, presupuestos, inversiones, transacciones } = useData();
  const modoEdicion = !!transaccionEditar;
  const [crearCuenta, setCrearCuenta] = useState(false);
  const sinCuentas = cuentasCargadas && cuentas.length === 0;

  const [tipo, setTipo]                       = useState<TipoForm>(transaccionEditar?.tipo || inicial?.tipo || 'gasto');
  const [cantidad, setCantidad]               = useState(transaccionEditar?.cantidad.toString() || inicial?.importe || '');
  const [categoriaId, setCategoriaId]         = useState(
    transaccionEditar?.adelanto ? '' : transaccionEditar?.categoriaId || ''
  );
  const [adelanto, setAdelanto]               = useState(transaccionEditar?.adelanto ?? false);
  const [persona, setPersona]                 = useState(transaccionEditar?.persona || '');
  const [cuentaId, setCuentaId]               = useState(transaccionEditar?.cuentaId || '');
  const [cuentaDestinoId, setCuentaDestinoId]       = useState('');
  const [destinoTipo, setDestinoTipo]               = useState<'cuenta' | 'inversion'>('cuenta');
  const [inversionDestinoId, setInversionDestinoId] = useState('');
  const [categoriaHuchaId, setCategoriaHuchaId]         = useState('');
  const [presupuestoHuchaId, setPresupuestoHuchaId]     = useState('');
  const [presupuestoDestinoId, setPresupuestoDestinoId] = useState('');
  const [descripcion, setDescripcion]         = useState(transaccionEditar?.descripcion || inicial?.descripcion || '');
  const [presupuestoId, setPresupuestoId]     = useState(transaccionEditar?.presupuestoId || '');
  const [fecha, setFecha]                     = useState(
    transaccionEditar
      ? new Date(transaccionEditar.fecha).toISOString().split('T')[0]
      : inicial?.fecha || new Date().toISOString().split('T')[0]
  );
  const [busquedaCat, setBusquedaCat]         = useState('');
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');
  // Comercio del que se ha copiado la categoría (para decirlo en el formulario)
  const [sugeridoDe, setSugeridoDe]           = useState('');
  // Los presupuestos pueden llegar después: se pone en cuanto se pueda elegir
  const [presupuestoSugerido, setPresupuestoSugerido] = useState('');

  // Con una sola cuenta no hay nada que elegir: va marcada de partida
  // (también la que se acaba de crear desde aquí mismo)
  useEffect(() => {
    if (!cuentaId && cuentas.length === 1) setCuentaId(cuentas[0].id);
  }, [cuentas, cuentaId]);

  // Pago de Apple Pay: la tarjeta con la que se pagó. Su cuenta se recuerda
  // (en Plus) y la primera vez se pregunta.
  const tarjeta = modoEdicion ? '' : inicial?.tarjeta ?? '';
  const cuentaTarjeta = tarjeta ? cuentaDeTarjeta(tarjeta, cuentas) : undefined;

  /**
   * Categoría, cuenta y presupuesto como la última vez en ese comercio. La
   * cuenta de la tarjeta de Wallet manda sobre la del comercio. Solo rellena
   * lo que está vacío.
   */
  const aplicarSugerencia = (texto: string) => {
    if (modoEdicion || tipo === 'traspaso' || adelanto) return;
    const deTarjeta = tarjeta ? cuentaDeTarjeta(tarjeta, cuentas) ?? cuentaParecida(tarjeta, cuentas) : undefined;
    if (deTarjeta) setCuentaId(deTarjeta.id);
    const sug = sugerenciaPorComercio(texto, tipo, transacciones);
    if (!sug || categoriaId) return;
    if (!categorias.some(c => c.id === sug.categoriaId && c.tipo === tipo)) return;
    setCategoriaId(sug.categoriaId);
    if (!cuentaId && !deTarjeta && cuentas.some(c => c.id === sug.cuentaId)) setCuentaId(sug.cuentaId);
    setPresupuestoSugerido(sug.presupuestoId ?? '');
    setSugeridoDe(texto.trim());
  };

  // Lo que llega de fuera: en cuanto estén los datos, una sola vez
  const sugerenciaInicialHecha = useRef(false);
  useEffect(() => {
    if (sugerenciaInicialHecha.current || !inicial || !cuentasCargadas || !categoriasCargadas || !transaccionesCargadas) return;
    sugerenciaInicialHecha.current = true;
    aplicarSugerencia(inicial.descripcion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicial, cuentasCargadas, categoriasCargadas, transaccionesCargadas]);

  const categoriasFiltradas = useMemo(
    () => categorias.filter(c => c.tipo === (tipo === 'ingreso' ? 'ingreso' : 'gasto')),
    [categorias, tipo],
  );
  const categoriasVisibles = useMemo(
    () => busquedaCat.trim()
      ? categoriasFiltradas.filter(c => c.nombre.toLowerCase().includes(busquedaCat.toLowerCase()))
      : categoriasFiltradas,
    [categoriasFiltradas, busquedaCat],
  );
  const categoriasGasto = categorias.filter(c => c.tipo === 'gasto');
  const personasConocidas = useMemo(() => personasDeAdelantos(transacciones), [transacciones]);
  const cuentasDestino  = cuentas.filter(c => c.id !== cuentaId);

  const destinoEsHucha =
    tipo === 'traspaso' && !!cuentaDestinoId &&
    cuentas.find(c => c.id === cuentaDestinoId)?.tipo === 'hucha';

  const presupuestosDisponibles = useMemo(() => {
    if (tipo !== 'gasto' || adelanto || !categoriaId) return [];
    // Derivamos el período desde la fecha de la transacción, no desde hoy.
    // Así, una transacción con fecha pasada encuentra sus presupuestos correctos.
    const fechaTx = new Date(fecha);

    return presupuestos.filter(p => {
      if (p.categoriaId !== categoriaId) return false;
      // Fuente de verdad compartida con la página de Presupuestos: un presupuesto
      // repetitivo aplica desde su inicio en adelante; uno puntual solo su período.
      const activo = presupuestoActivoEnPeriodo(p, periodoDeFecha(fechaTx, p.periodicidad));
      // En edición conservamos el vínculo original aunque el presupuesto ya no
      // aplique al período (p. ej. una transacción antigua que se reedita).
      if (modoEdicion && p.id === transaccionEditar?.presupuestoId) return true;
      return activo;
    });
  }, [tipo, adelanto, categoriaId, presupuestos, fecha, modoEdicion, transaccionEditar]);

  useEffect(() => {
    if (!presupuestoSugerido || presupuestoId) return;
    if (presupuestosDisponibles.some(p => p.id === presupuestoSugerido)) {
      setPresupuestoId(presupuestoSugerido);
      setPresupuestoSugerido('');
    }
  }, [presupuestoSugerido, presupuestoId, presupuestosDisponibles]);

  const presupuestosHucha = useMemo(() => {
    if (!categoriaHuchaId) return [];
    const fechaTx = new Date(fecha);
    return presupuestos.filter(p =>
      p.categoriaId === categoriaHuchaId &&
      presupuestoActivoEnPeriodo(p, periodoDeFecha(fechaTx, p.periodicidad)),
    );
  }, [categoriaHuchaId, presupuestos, fecha]);

  // Presupuestos de ahorro anuales disponibles para vincular al traspaso a inversión
  const presupuestosAhorro = useMemo(() => {
    const fechaTx = new Date(fecha);
    const anioTx  = String(fechaTx.getFullYear());
    return presupuestos.filter(
      p => p.esObjetivoAhorro === true &&
           p.periodicidad === 'anual' &&
           presupuestoActivoEnPeriodo(p, anioTx),
    );
  }, [presupuestos, fecha]);

  // Preview de comisión para el traspaso a inversión
  const invSeleccionada = inversiones.find(i => i.id === inversionDestinoId);
  const previewComision = useMemo(() => {
    if (!invSeleccionada || !cantidad || tipo !== 'traspaso' || destinoTipo !== 'inversion') return null;
    return calcularComisionAportacion(
      invSeleccionada.comision,
      new Date(invSeleccionada.fechaInicio),
      new Date(fecha),
      aNumero(cantidad) || 0,
    );
  }, [invSeleccionada, cantidad, tipo, destinoTipo, fecha]);

  const inversionesActivas = inversiones.filter(i => i.activo);

  const handleChangeTipo = (t: TipoForm) => {
    setTipo(t);
    setCategoriaId('');
    setCuentaDestinoId('');
    setPresupuestoId('');
    setCategoriaHuchaId('');
    setPresupuestoHuchaId('');
    setBusquedaCat('');
    setDestinoTipo('cuenta');
    setInversionDestinoId('');
    setPresupuestoDestinoId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!cantidad || !cuentaId) { setError('Completa todos los campos obligatorios'); return; }
    if (tipo !== 'traspaso' && !adelanto && !categoriaId) { setError('Selecciona una categoría'); return; }
    if (tipo !== 'traspaso' && adelanto && !persona.trim()) { setError('Indica la persona del adelanto'); return; }
    if (tipo === 'traspaso') {
      if (destinoTipo === 'cuenta') {
        if (!cuentaDestinoId) { setError('Selecciona la cuenta destino'); return; }
        if (cuentaId === cuentaDestinoId) { setError('Las cuentas de origen y destino no pueden ser la misma'); return; }
      } else {
        if (!inversionDestinoId) { setError('Selecciona el producto de inversión destino'); return; }
      }
      const origen = cuentas.find(c => c.id === cuentaId);
      if (origen && aNumero(cantidad) > origen.saldo && !(origen.esCredito ?? false)) {
        setError(`Saldo insuficiente en la cuenta de origen (€${origen.saldo.toFixed(2)})`); return;
      }
    }
    setLoading(true);
    try {
      if (tipo === 'traspaso') {
        if (destinoTipo === 'inversion') {
          await crearTraspasoAInversion({
            cuentaOrigenId:     cuentaId,
            inversionDestinoId: inversionDestinoId,
            cantidad:           aNumero(cantidad),
            descripcion,
            fecha:              new Date(fecha),
            ...(presupuestoDestinoId ? { presupuestoDestinoId } : {}),
          });
        } else {
          await crearTraspaso({
            cuentaOrigenId: cuentaId, cuentaDestinoId,
            cantidad: aNumero(cantidad), descripcion,
            fecha: new Date(fecha),
            ...(categoriaHuchaId  ? { categoriaOrigenId:  categoriaHuchaId  } : {}),
            ...(presupuestoHuchaId ? { presupuestoOrigenId: presupuestoHuchaId } : {}),
          });
        }
      } else if (modoEdicion && transaccionEditar) {
        await actualizarTransaccion(transaccionEditar.id, {
          tipo, cantidad: aNumero(cantidad),
          categoriaId: adelanto ? CATEGORIA_ADELANTO : categoriaId, cuentaId,
          descripcion, fecha: new Date(fecha), adelanto,
          ...(adelanto ? { persona: persona.trim() } : { presupuestoId: presupuestoId || undefined }),
        }, transaccionEditar);
      } else {
        await crearTransaccion({
          tipo, cantidad: aNumero(cantidad),
          categoriaId: adelanto ? CATEGORIA_ADELANTO : categoriaId, cuentaId,
          descripcion, fecha: new Date(fecha),
          ...(adelanto ? { adelanto: true, persona: persona.trim() } : presupuestoId ? { presupuestoId } : {}),
        });
      }
      // Sin esperar: con el gasto guardado basta, y sin red se sube luego
      if (tarjeta && esPlus && tipo !== 'traspaso') {
        cambiosTarjeta(tarjeta, cuentaId, cuentas).forEach(({ id, tarjetasApplePay }) =>
          actualizarCuenta(id, { tarjetasApplePay })
            .catch(e => console.error('No se pudo recordar la cuenta de la tarjeta', e)));
      }
      vibrar.exito();
      onClose();
    } catch (err: any) {
      vibrar.error();
      setError(err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm";
  const labelClass = "block text-gray-700 dark:text-gray-300 font-semibold mb-1.5 text-sm";

  // Categoría seleccionada (para mostrar confirmación)
  const catSeleccionada = categorias.find(c => c.id === categoriaId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center capa-hoja z-50">
      <div className="hoja bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-full overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {modoEdicion ? 'Editar transacción' : inicial ? (tipo === 'ingreso' ? 'Apuntar ingreso' : 'Apuntar gasto') : 'Nueva transacción'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          {transaccionEditar?.planId && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800
                            text-emerald-800 dark:text-emerald-300 px-3 py-2 rounded-lg text-xs">
              Viene de un plan de A medias. Puedes cambiar la cuenta, la categoría o el presupuesto;
              el importe, la fecha y la descripción se actualizan desde el plan.
            </div>
          )}

          {/* Tipo */}
          <div>
            <label className={labelClass}>Tipo</label>
            <div className="flex gap-2">
              {!modoEdicion ? (
                <>
                  {([
                    { t: 'ingreso' as TipoForm,  label: 'Ingreso',  cls: 'bg-green-600 hover:bg-green-700'  },
                    { t: 'gasto'   as TipoForm,  label: 'Gasto',    cls: 'bg-red-600 hover:bg-red-700'      },
                    { t: 'traspaso' as TipoForm, label: 'Traspaso', cls: 'bg-purple-600 hover:bg-purple-700' },
                  ]).map(({ t, label, cls }) => (
                    <button key={t} type="button" onClick={() => handleChangeTipo(t)}
                      className={`flex-1 py-2 rounded-lg font-semibold transition text-sm flex items-center justify-center gap-1 ${
                        tipo === t ? `${cls} text-white` : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}>
                      {t === 'traspaso' && <ArrowRightLeft className="w-3.5 h-3.5" />}
                      {label}
                    </button>
                  ))}
                </>
              ) : (
                <div className="flex gap-4">
                  {(['ingreso', 'gasto'] as const).map(t => (
                    <label key={t} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" value={t} checked={tipo === t}
                        onChange={() => handleChangeTipo(t)} className="accent-blue-600" />
                      <span className={`font-semibold text-sm ${t === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                        {t === 'ingreso' ? 'Ingreso' : 'Gasto'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cantidad */}
          <div>
            <label className={labelClass}>Cantidad (€)</label>
            <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE_POSITIVO} title={AVISO_IMPORTE} value={cantidad}
              onChange={e => setCantidad(e.target.value)}
              className={`${inputClass} text-lg font-bold`}
              placeholder="0,00" required />
          </div>

          {/* Cuenta origen */}
          <div>
            <label className={labelClass}>{tipo === 'traspaso' ? 'Cuenta origen' : 'Cuenta'}</label>
            {sinCuentas ? (
              // Sin cuentas el desplegable sale vacío y no deja guardar: mejor decirlo
              <div className="rounded-xl border border-dashed border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Aún no tienes ninguna cuenta. Los gastos e ingresos se apuntan en una cuenta
                  (el banco, el efectivo…), así que primero crea una.
                </p>
                <button type="button" onClick={() => setCrearCuenta(true)}
                  className="mt-3 inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl bg-blue-600 hover:bg-blue-700
                             text-white text-sm font-semibold transition">
                  <Wallet className="w-4 h-4" /> Crear mi primera cuenta
                </button>
              </div>
            ) : (
            <select value={cuentaId}
              onChange={e => { setCuentaId(e.target.value); setCuentaDestinoId(''); }}
              className={inputClass} required>
              <option value="">Selecciona una cuenta</option>
              {cuentas.map(c => (
                <option key={c.id} value={c.id}>
                  {c.icono ? `${c.icono} ` : ''}{c.nombre}
                  {tipo === 'traspaso' ? ` — €${c.saldo.toFixed(2)}` : ''}
                </option>
              ))}
            </select>
            )}
            {tarjeta && tipo !== 'traspaso' && !sinCuentas && (
              cuentaTarjeta && cuentaTarjeta.id === cuentaId ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  La cuenta de tu tarjeta «{tarjeta}».
                </p>
              ) : (
                <div className="mt-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-900 dark:text-blue-200">
                  {cuentaTarjeta
                    ? <>Has cambiado la cuenta de la tarjeta «{tarjeta}».</>
                    : <>Primera vez que pagas con «{tarjeta}». Elige su cuenta.</>}
                  {' '}
                  {esPlus ? (
                    'La próxima vez ya vendrá puesta.'
                  ) : (
                    <button type="button" onClick={() => ofrecerPlus('applePay')} className="font-semibold underline">
                      Con Plus, myarinn la recuerda.
                    </button>
                  )}
                </div>
              )
            )}
          </div>

          {/* Destino — traspaso */}
          {tipo === 'traspaso' && (
            <div className="space-y-3">
              {/* Toggle cuenta / inversión */}
              <div>
                <label className={labelClass}>Destino</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setDestinoTipo('cuenta'); setInversionDestinoId(''); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm transition ${
                      destinoTipo === 'cuenta'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                    <ArrowRightLeft className="w-3.5 h-3.5" /> Cuenta bancaria
                  </button>
                  <button type="button" onClick={() => { setDestinoTipo('inversion'); setCuentaDestinoId(''); setCategoriaHuchaId(''); setPresupuestoHuchaId(''); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm transition ${
                      destinoTipo === 'inversion'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                    <TrendingUp className="w-3.5 h-3.5" /> Inversión
                  </button>
                </div>
              </div>

              {/* Selector cuenta */}
              {destinoTipo === 'cuenta' && (
                <div>
                  <select value={cuentaDestinoId}
                    onChange={e => { setCuentaDestinoId(e.target.value); setCategoriaHuchaId(''); setPresupuestoHuchaId(''); }}
                    className={inputClass} required>
                    <option value="">Selecciona la cuenta destino</option>
                    {cuentasDestino.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.tipo === 'hucha' ? '🐷 ' : c.icono ? `${c.icono} ` : ''}{c.nombre} — €{c.saldo.toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selector inversión + preview comisión + objetivo de ahorro */}
              {destinoTipo === 'inversion' && (
                <div className="space-y-2">
                  <select value={inversionDestinoId}
                    onChange={e => { setInversionDestinoId(e.target.value); setPresupuestoDestinoId(''); }}
                    className={inputClass} required>
                    <option value="">Selecciona el producto de inversión</option>
                    {inversionesActivas.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {TIPO_INVERSION_ICON[inv.tipo]} {inv.nombre} — {inv.broker}
                      </option>
                    ))}
                  </select>

                  {previewComision && aNumero(cantidad) > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-2 text-xs space-y-0.5">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Aportas</span>
                        <span className="font-semibold text-gray-900 dark:text-white">€{(aNumero(cantidad) || 0).toFixed(2)}</span>
                      </div>
                      {previewComision.comisionAplicada > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Comisión ({previewComision.porcentaje}%)</span>
                          <span className="font-semibold text-orange-600">−€{previewComision.comisionAplicada.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-blue-200 dark:border-blue-700 pt-0.5 mt-0.5">
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">Capital que se invierte</span>
                        <span className="font-bold text-blue-700 dark:text-blue-300">€{previewComision.cantidadInvertida.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Objetivo de ahorro asociado */}
                  {inversionDestinoId && (
                    <div>
                      <label className={labelClass}>
                        <span className="flex items-center gap-1.5">
                          <PiggyBank className="w-3.5 h-3.5 text-purple-500" />
                          Objetivo de ahorro
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(opcional)</span>
                        </span>
                      </label>
                      {presupuestosAhorro.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded px-2 py-1.5">
                          No hay objetivos de ahorro anuales para {new Date(fecha).getFullYear()}.
                        </p>
                      ) : (
                        <select value={presupuestoDestinoId}
                          onChange={e => setPresupuestoDestinoId(e.target.value)}
                          className={inputClass}>
                          <option value="">Sin objetivo (inversión libre)</option>
                          {presupuestosAhorro.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.nombre} — objetivo €{p.limite.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info hucha */}
          {destinoEsHucha && (
            <>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                  🐷 Traspaso a hucha — indica el concepto para imputarlo al presupuesto
                </p>
              </div>
              <div>
                <label className={labelClass}>Categoría del gasto</label>
                <select value={categoriaHuchaId}
                  onChange={e => { setCategoriaHuchaId(e.target.value); setPresupuestoHuchaId(''); }}
                  className={inputClass}>
                  <option value="">Sin categoría específica</option>
                  {categoriasGasto.map(c => (
                    <option key={c.id} value={c.id}>{c.icono} {c.nombre}</option>
                  ))}
                </select>
              </div>
              {presupuestosHucha.length > 0 && (
                <div>
                  <label className={labelClass}>
                    Imputar a presupuesto
                    <span className="ml-1 text-xs font-normal text-gray-400">(opcional)</span>
                  </label>
                  <select value={presupuestoHuchaId} onChange={e => setPresupuestoHuchaId(e.target.value)}
                    className={inputClass}>
                    <option value="">Sin presupuesto</option>
                    {presupuestosHucha.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre} — €{limiteEnPeriodo(p, periodoDeFecha(new Date(fecha), p.periodicidad)).toFixed(2)}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* ── Adelanto: pagado por otros / devuelto ─────────────────────── */}
          {tipo !== 'traspaso' && (
            <label
              title={tipo === 'gasto'
                ? 'Lo pagas por otra persona y te lo devolverá. Mueve el saldo de la cuenta, pero no cuenta como gasto.'
                : 'Te devuelven algo que pagaste por otros. Mueve el saldo, pero no cuenta como ingreso.'}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 cursor-pointer transition text-sm font-semibold ${
                adelanto
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 text-amber-800 dark:text-amber-300'
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
              }`}>
              <input type="checkbox" checked={adelanto}
                onChange={e => { setAdelanto(e.target.checked); setPresupuestoId(''); }}
                className="accent-amber-600" />
              <HelpingHand className="w-4 h-4 text-amber-600" />
              {tipo === 'gasto' ? 'Adelanto' : 'Devolución de adelanto'}
            </label>
          )}

          {tipo !== 'traspaso' && adelanto && (
            <div>
              <label className={labelClass}>{tipo === 'gasto' ? '¿A quién se lo adelantas?' : '¿Quién te lo devuelve?'}</label>
              <input type="text" value={persona} onChange={e => setPersona(e.target.value)}
                list="personas-adelanto" className={inputClass}
                placeholder="Padres, Ana…" required />
              <datalist id="personas-adelanto">
                {personasConocidas.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
          )}

          {/* ── Selector visual de categoría ───────────────────────────────── */}
          {tipo !== 'traspaso' && !adelanto && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={`${labelClass} mb-0`}>Categoría</label>
                {catSeleccionada && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white truncate max-w-[140px]"
                    style={{ backgroundColor: catSeleccionada.color }}>
                    {catSeleccionada.icono} {catSeleccionada.nombre}
                  </span>
                )}
              </div>
              {sugeridoDe && catSeleccionada && (
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-0.5 mb-1.5">
                  Como la última vez en «{sugeridoDe}». Toca otra si no es esta.
                </p>
              )}

              {categoriasFiltradas.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  No hay categorías de tipo {tipo}. Créalas en{' '}
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">Configuración</span>.
                </p>
              ) : (
                <>
                  {/* Búsqueda si hay muchas */}
                  {categoriasFiltradas.length > 8 && (
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" />
                      <input
                        type="text" value={busquedaCat}
                        onChange={e => setBusquedaCat(e.target.value)}
                        placeholder="Buscar categoría…"
                        className={`${inputClass} pl-8`}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto p-0.5">
                    {categoriasVisibles.length === 0 ? (
                      <p className="col-span-full text-center text-xs text-gray-400 py-3">Sin resultados</p>
                    ) : (
                      categoriasVisibles.map(cat => {
                        const selected = categoriaId === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => { setCategoriaId(cat.id); setPresupuestoId(''); setSugeridoDe(''); setPresupuestoSugerido(''); }}
                            className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all text-center active:scale-95 ${
                              selected
                                ? 'border-current shadow-sm'
                                : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                            }`}
                            style={{
                              backgroundColor: cat.color + (selected ? '30' : '15'),
                              borderColor:     selected ? cat.color : undefined,
                            }}
                          >
                            <span className="text-2xl leading-none">{cat.icono}</span>
                            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-200 leading-tight line-clamp-2">
                              {cat.nombre}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Presupuesto */}
          {tipo === 'gasto' && !adelanto && presupuestosDisponibles.length > 0 && (
            <div>
              <label className={labelClass}>
                Imputar a presupuesto
                <span className="ml-1 text-xs font-normal text-gray-400">(opcional)</span>
              </label>
              <select value={presupuestoId} onChange={e => setPresupuestoId(e.target.value)} className={inputClass}>
                <option value="">Sin presupuesto</option>
                {presupuestosDisponibles.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre} — €{limiteEnPeriodo(p, periodoDeFecha(new Date(fecha), p.periodicidad)).toFixed(2)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Fecha */}
          <div>
            <label className={labelClass}>Fecha</label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
              className={inputClass} required />
          </div>

          {/* Descripción */}
          <div>
            <label className={labelClass}>Descripción <span className="text-xs font-normal text-gray-400">(opcional)</span></label>
            <input type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)}
              onBlur={() => { if (!categoriaId) aplicarSugerencia(descripcion); }}
              className={inputClass}
              placeholder={tipo === 'traspaso' ? 'Motivo del traspaso' : adelanto ? 'Qué es (p. ej. seguro del coche)' : 'Detalles…'} />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-1 pb-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className={`flex-1 py-2.5 text-white font-semibold rounded-xl transition disabled:opacity-50 text-sm ${
                tipo === 'traspaso' ? 'bg-purple-600 hover:bg-purple-700'
                : tipo === 'ingreso' ? 'bg-green-600 hover:bg-green-700'
                : 'bg-blue-600 hover:bg-blue-700'
              }`}>
              {loading ? 'Guardando…'
                : modoEdicion ? 'Actualizar'
                : tipo === 'traspaso' ? 'Realizar traspaso'
                : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
      {crearCuenta && <ModalAgregarCuenta onClose={() => setCrearCuenta(false)} />}
    </div>
  );
};
